import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Flex, Spinner, Text } from '@radix-ui/themes'

import { Alert, Info } from './MessageInfo'
import { SettlementPreview } from './SettlementPreview'
import SubMenuSettlement from './SubMenuSettlement'

import { ExpenseFormat } from '@/interfaces/VehicleBase'
import Settlement from '@/interfaces/Settlement'
import { transformToExpenseFormat } from '@/utils/utils'
import useSettlementFetcher from '@/hooks/useSettlementFetcher'
import { useAuth } from '@/hooks/useAuth'
import useSettlementVoid from '@/hooks/useSettlementVoid'
import useVehicleFetcher from '@/hooks/useVehicleFetcher'
import useClientFetcher from '@/hooks/useClientFetcher'
import { useConsignmentFetcher } from '@/hooks/useConsignmentFetcher'
import Client from '@/interfaces/Client'
import Vehicle from '@/interfaces/Vehicle'
import ConsignmentDet from '@/interfaces/ConsignmentDet'
import Consignment from '@/interfaces/Consignment'
import { AppPath } from '@/enums/AppPath'
import { MessageCrea } from '@/utils/utils'
import { PurchaseAction } from '@/enums/DocActions'

export const SettlementDetail = () => {
  const { fetchSettlement, errorSettlement } = useSettlementFetcher()
  const { settlementVoid } = useSettlementVoid()
  const [settlement, setSettlement] = useState<Settlement>()
  const [localError, setLocalError] = useState<string>()
  const [expenses, setExpenses] = useState<ExpenseFormat[]>([])
  const { userData } = useAuth()
  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const [refreshKey, setRefreshKey] = useState(0) // For refreshing data
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const { errorClient, fetchClient } = useClientFetcher()
  const { fetchConsignment } = useConsignmentFetcher()
  const [vehicle, setVehicle] = useState<Vehicle>()
  const [client, setClient] = useState<Client>()
  const [consignmentDet, setConsignmentDet] = useState<ConsignmentDet>()
  const navigate = useNavigate()

  // Create adapter for ConsignmentDet to Consignment
  const consignmentAdapter: Consignment | undefined = consignmentDet
    ? {
        _id: settlement?.consignment ?? '',
        body: {
          organization: {
            addressComponents: '',
            representatives: '',
            streetNumber: 0,
            legalName: consignmentDet.organization.legalName,
            business: consignmentDet.organization.business,
            address: consignmentDet.organization.address,
            domain: consignmentDet.organization.domain,
            email: consignmentDet.organization.email,
            phone: consignmentDet.organization.phone,
            name: consignmentDet.organization.name,
            nin: consignmentDet.organization.nin,
          },
          vehicle: consignmentDet.vehicle,
          client: {
            firstname: consignmentDet.client.firstname,
            lastname: consignmentDet.client.lastname,
            phone: consignmentDet.client.phone,
            nin: consignmentDet.client.nin,
            address: consignmentDet.client.address,
            email: consignmentDet.client.email,
            gender: 'Masculino',
            streetNumber: 0,
            addressComponents: '',
          },
          profile: settlement?.body.profile || { user: { firstname: '', lastname: '', nin: '' }, email: '' },
        },
        price: consignmentDet.price,
        notes: [consignmentDet.notes],
        minimum: consignmentDet.minimum,
        charge: consignmentDet.charge,
        fee: consignmentDet.fee,
        vehicle: settlement?.vehicle ?? '',
        client: settlement?.client ?? '',
        organization: settlement?.organization ?? '',
        profile: settlement?.profile ?? '',
        city: consignmentDet.city,
        date: new Date(consignmentDet.date),
        createdAt: settlement ? new Date(settlement.createdAt) : new Date(),
        updatedAt: settlement ? new Date(settlement.updatedAt) : new Date(),
        __v: settlement?.__v ?? 0,
        paymentShow: consignmentDet.paymentShow,
        feeIva: consignmentDet.feeIva,
        minimumIva: consignmentDet.minimumIva,
      }
    : undefined

  useEffect(() => {
    fetchDataAsync()
  }, [id, refreshKey])

  const fetchDataAsync = async () => {
    if (!id) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined
    }
    const settlement: Settlement | null = await fetchSettlement(id)

    if (!settlement) return
    setSettlement(settlement)
    const expenses = transformToExpenseFormat(settlement.body.vehicle.expenses ?? [])
    setExpenses(expenses)

    // Fetch related data for edit mode
    if (settlement.consignment) {
      const consignmentId = settlement.consignment
      const consignment = await fetchConsignment(consignmentId)

      if (!consignment) {
        setErrorMessage('Error recuperando la consignación')

        return
      }
      setConsignmentDet(consignment)
    }

    // Fetch vehicle and client for edit mode
    const vehicleId = settlement.vehicle
    const vehicle = await fetchVehicle(vehicleId)

    if (errorVehicle) {
      setErrorMessage(errorVehicle.message)

      return
    }

    if (!vehicle) {
      setErrorMessage('Error recuperando el vehículo')

      return
    }

    const clientId = settlement.client
    const client = await fetchClient(clientId)

    if (errorClient) {
      setErrorMessage(errorClient.message)

      return
    }

    if (!client) {
      setErrorMessage('Error recuperando el cliente')

      return
    }

    setVehicle(vehicle)
    setClient(client)
  }


  const onAnular = async (motivo: string) => {
    const by = userData?.profile?._id ?? ''
    const resp = await settlementVoid(id ?? '', motivo, by)

    if (resp) {
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const msg = MessageCrea(AppPath.Settlements, PurchaseAction.Anular)
      setInfoMessage(msg)

      // Refresh data
      handleRefresh()
    }
  }

  const handleRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  const infoMessageTimeout = (): void => {
    setInfoMessage('')
  }

  // Add debugging info
  useEffect(() => {
    console.log('SettlementDetail debug:', {
      consignmentId: consignmentAdapter?._id,
      settlementId: settlement?._id,
      hasAnularFunction: !!onAnular,
      hasRefreshFunction: !!handleRefresh,
    })
  }, [settlement, consignmentAdapter])

  const handleGoBackEdit = () => {
    navigate(`/${AppPath.Settlements}/edit/${settlement?._id}`)
  }

  if (localError || errorSettlement) {
    return (
      <Container size="3">
        <Flex direction="row" gap="2">
          <Spinner />
          <Text>Cargando datos...</Text>
        </Flex>

        {localError && <p className="text-red-500">{localError}</p>}
        {errorSettlement && <p className="text-red-500">{errorSettlement.message}</p>}
      </Container>
    )
  }

  if (!settlement || !vehicle || !client) {
    return (
      <Flex>
        <Spinner />
      </Flex>
    )
  }

  return (
    <Container size="3">
      {errorMessage !== '' && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      {infoMessage !== '' && <Info message={infoMessage} trigger={Date.now()} onTimeout={infoMessageTimeout} />}

      <SettlementPreview vehicle={vehicle} client={client} settlement={settlement} costs={expenses} consultaSw={true} />

      {!settlement.voidedAt && (
        <SubMenuSettlement
          handleGoBackEdit={handleGoBackEdit}
          handleAnular={onAnular}
          esAnulada={!!settlement?.voidedAt}
        />
      )}

      {settlement.voidedAt && (
        <div className="mt-4 bg-gray-100 border border-gray-300 p-4 rounded">
          <h3 className="text-xl font-bold text-red-500 mb-2">Liquidación Anulada</h3>
          <p>
            <strong>Fecha de anulación:</strong> {new Date(settlement.voidedAt).toLocaleDateString()}
          </p>
          <p>
            <strong>Motivo:</strong> {settlement.voidedCause || 'No especificado'}
          </p>
          <p>
            <strong>Anulado por:</strong> {settlement.voidedBy_user || 'No especificado'}
          </p>
        </div>
      )}
    </Container>
  )
}
